/**
 * @file redelivery.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-10-21
 * @description Refused deliveries and redelivery — the one set of rules the
 *              order screens, the planning boards and the server share (Cam,
 *              15 Oct 2026; SO-D10289, refused by Ashmead Farming on 7 Oct and
 *              stored at J. Pickard & Co. until the customer had room).
 *
 *   A REDELIVERY is a new leg recorded against a customer order that was
 *   refused on its delivery day: the goods are held somewhere (our own site —
 *   STOCK — or a contact's yard) and delivered again on a later day. The
 *   order and its PO-D stay exactly as they are: nothing is bought again, the
 *   supplied quantity stays, bills and costs are untouched, and the refused
 *   day's snapshot, Plan History and paperwork stay the record of that day.
 *
 *   - Status: 'held' (no date yet), 'planned' (a date), 'delivered' (its date
 *     has passed, London), 'cancelled'. A planned one whose day has passed
 *     READS delivered whether or not the database has caught up
 *     (effectiveRedeliveryStatus).
 *   - On its date each line is a planning-board row keyed
 *     `rd:<redelivery id>:<order line id>` in the boards' row-id slot
 *     (order_line_id): collected from where it is held, delivered to the
 *     order's customer. Its haulier, driver and vehicle are the
 *     redelivery's own — never the order line's or the PO's, which keep the
 *     original delivery's. It is never sent to a mill (nothing is bought).
 *   - While one is open the order's delivery status READS "Held for
 *     Redelivery" / "Redelivery" (the stored status stays Delivered, so every
 *     after-delivery lock still holds) and it cannot be invoiced.
 *
 *   The server's utils/redelivery.js is GENERATED from this file by
 *   scripts/sync-shared-utils.js — edit this one and re-run it.
 */

import { ukDay, londonToday } from './transportLock';

export const REDELIVERY_LABEL = 'Redelivery';
export const REDELIVERY_STATUS_HELD = 'held';
export const REDELIVERY_STATUS_PLANNED = 'planned';
export const REDELIVERY_STATUS_DELIVERED = 'delivered';
export const REDELIVERY_STATUS_CANCELLED = 'cancelled';
export const REDELIVERY_OPEN_STATUSES = ['held', 'planned'];

/** The order's delivery status while a redelivery is open (≤ 30 characters — the column's width). */
export const DELIVERY_STATUS_HELD = 'Held for Redelivery';
export const DELIVERY_STATUS_REDELIVERY = 'Redelivery';

/** Our own site, as the self-billed PO's STOCK option names it. */
export const REDELIVERY_STOCK_VALUE = 'stock';
export const REDELIVERY_STOCK_LABEL = 'STOCK — Excel Agri Ltd.';

export const REDELIVERY_NOT_READY =
  'Not ready — migration order_redeliveries.sql has not run';
export const REDELIVERY_OUT_OF_SCOPE =
  'Not in this version: charging the customer for the extra delivery or storage (add a line to the order or invoice by hand), the store’s own storage bill (link it to the PO-D with Add Bill’s “Haulage for a PO”), part quantities (the whole line goes) and a third-party haulier for the redelivery.';

/** Why a board row of a redelivery is not moved like an order row. */
export const REDELIVERY_DATE_REASON = 'A redelivery’s date is changed in its Redelivery window (Edit redelivery…)';
export const REDELIVERY_BOARD_REASON = 'A redelivery goes on the board its order line is on — it is not moved by hand';
export const REDELIVERY_QTY_REASON = 'A redelivery takes the whole line — its quantity is the supplied quantity';
export const REDELIVERY_ARCHIVE_REASON = 'a redelivery — cancel the redelivery (Edit redelivery…) rather than archive from this row';

const text = (v) => String(v == null ? '' : v).trim();
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const dayOf = (d) => {
  const s = d == null ? '' : String(d).slice(0, 10);
  return DAY.test(s) ? s : null;
};

/** 'DD/MM/YY' for a 'YYYY-MM-DD'. */
export function ukShortDay(date) {
  const d = dayOf(date);
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y.slice(2)}`;
}

/*
 * ── Board rows ──────────────────────────────────────────────────────────
 */

export const redeliveryRowId = (redeliveryId, orderLineId) => `rd:${redeliveryId}:${orderLineId}`;
export const isRedeliveryRowId = (id) => /^rd:\d+:\d+$/.test(String(id == null ? '' : id));

/** { redeliveryId, orderLineId } for a board row key, or null. */
export function parseRedeliveryRowId(id) {
  const m = /^rd:(\d+):(\d+)$/.exec(String(id == null ? '' : id));
  return m ? { redeliveryId: Number(m[1]), orderLineId: Number(m[2]) } : null;
}

/**
 * The order line a board row is stored against: its own for an order row, the
 * real order line for a redelivery row (whose key is `rd:…`). A redelivery
 * row's collection time, board order, door layout, run and lorry day plan
 * are stored by that order line on the redelivery's date, as a stock row's
 * are — the order's own day is always an earlier one, so they never meet.
 */
export function storageLineIdOf(row) {
  if (!row) return null;
  if (row.is_redelivery) {
    const p = parseRedeliveryRowId(row.order_line_id);
    if (p) return p.orderLineId;
    const n = Number(row.source_order_line_id);
    return Number.isInteger(n) ? n : null;
  }
  return row.order_line_id;
}

/** Has a redelivery row a haulier, driver or vehicle of ours? */
export function redeliveryRowPlanned(row) {
  if (!row || !row.is_redelivery) return true;
  return !!(text(row.haulier) || text(row.driver) || text(row.vehicle));
}

/*
 * ── Status ──────────────────────────────────────────────────────────────
 */

/**
 * A redelivery's status as it stands today (London): a planned one whose
 * day has passed is delivered.
 * @param {{ status, redelivery_date }} r
 */
export function effectiveRedeliveryStatus(r, today = londonToday()) {
  if (!r) return null;
  const s = text(r.status).toLowerCase();
  if (s === REDELIVERY_STATUS_PLANNED) {
    const d = dayOf(r.redelivery_date);
    return d && d < String(today) ? REDELIVERY_STATUS_DELIVERED : REDELIVERY_STATUS_PLANNED;
  }
  return s || null;
}

/** Is it still to happen (held, or planned for today or later)? */
export function isOpenRedelivery(r, today = londonToday()) {
  return REDELIVERY_OPEN_STATUSES.includes(effectiveRedeliveryStatus(r, today));
}

/** Where it is held, as words: "J. Pickard & Co. (Burrington) Ltd (2286)" or "STOCK — Excel Agri Ltd.". */
export function heldAtLabel(r) {
  if (!r) return '';
  if (r.held_at_stock) return REDELIVERY_STOCK_LABEL;
  const name = text(r.held_at_name);
  if (!name) return r.held_at_account_id ? `contact #${r.held_at_account_id}` : '';
  return r.held_at_account_no ? `${name} (${r.held_at_account_no})` : name;
}

/**
 * The order's delivery status while a redelivery is open, or null (the
 * stored status stands): "Held for Redelivery" with no date, "Redelivery"
 * with one.
 */
export function redeliveryDeliveryStatus(r, today = londonToday()) {
  const s = effectiveRedeliveryStatus(r, today);
  if (s === REDELIVERY_STATUS_HELD) return DELIVERY_STATUS_HELD;
  if (s === REDELIVERY_STATUS_PLANNED) return DELIVERY_STATUS_REDELIVERY;
  return null;
}

/** The pill's words: "Held at <store>", "Redelivery 21/10/26", "Redelivered 21/10/26". */
export function redeliveryPillText(r, today = londonToday()) {
  const s = effectiveRedeliveryStatus(r, today);
  if (s === REDELIVERY_STATUS_HELD) {
    const at = r.held_at_stock ? 'our site' : (text(r.held_at_name) || 'store');
    return `Held at ${at}`;
  }
  if (s === REDELIVERY_STATUS_PLANNED) return `${REDELIVERY_LABEL} ${ukShortDay(r.redelivery_date)}`;
  if (s === REDELIVERY_STATUS_DELIVERED) return `Redelivered ${ukShortDay(r.redelivery_date)}`;
  return '';
}

/** Hover text: refused on, why, where it is held, when it goes again. */
export function redeliveryTitle(r, today = londonToday()) {
  if (!r) return '';
  const s = effectiveRedeliveryStatus(r, today);
  const lines = [
    `Refused on ${ukShortDay(r.refused_on)}${text(r.reason) ? ` — ${text(r.reason)}` : ''}`,
    `Held at ${heldAtLabel(r) || 'an unknown store'}`,
    s === REDELIVERY_STATUS_HELD ? 'Redelivery date to follow'
      : s === REDELIVERY_STATUS_PLANNED ? `Redelivery on ${ukShortDay(r.redelivery_date)}`
        : s === REDELIVERY_STATUS_DELIVERED ? `Redelivered on ${ukShortDay(r.redelivery_date)}`
          : s === REDELIVERY_STATUS_CANCELLED ? 'Redelivery cancelled' : ''
  ];
  return lines.filter(Boolean).join('\n');
}

/** What the driver's top sheet and despatch note say about it. */
export function redeliveryPaperworkNote({ orderNumber, refusedOn, heldAt } = {}) {
  const parts = [`Redelivery of ${text(orderNumber) || 'the order'}${dayOf(refusedOn) ? ` (refused ${ukShortDay(refusedOn)})` : ''}`];
  if (text(heldAt)) parts.push(`collect from ${text(heldAt)}`);
  return parts.join(' — ');
}

/*
 * ── May this order be redelivered? ──────────────────────────────────────
 */

const THIRD_PARTY = 'Third-Party Delivery';
const DIRECT_DELIVERY = 'Direct Delivery';
const CUSTOMER_COLLECTION = 'Customer Collection';
const NOT_DESPATCHED = 'Not Despatched';

/**
 * What "Refused — redeliver…" does for an order now: start one ('new'),
 * change the open one ('edit'), or nothing, with the reason.
 *
 * @param {object} order  order_number, delivery_date, delivery_status,
 *   invoiced_status, draft_invoice_no, is_archived, supplier_delivery_terms,
 *   excel_agri_delivery_terms, has_supply_pos (false for an all-stock order),
 *   redelivery (the latest, or null)
 * @param {object} ctx    { today, ready, canEdit, readOnly }
 * @returns {{ mode: 'new'|'edit'|null, block: string|null }}
 */
export function redeliverAction(order, { today = londonToday(), ready = true, canEdit = true, readOnly = false } = {}) {
  if (!order) return { mode: null, block: 'No customer order' };
  const num = text(order.order_number) || 'This order';
  if (!ready) return { mode: null, block: REDELIVERY_NOT_READY };
  if (readOnly) return { mode: null, block: 'View-only accounts cannot record a refused delivery' };
  if (!canEdit) return { mode: null, block: 'Needs Edit Transport Planning or Edit sales orders' };
  if (order.is_archived) return { mode: null, block: `${num} is archived — restore it first` };
  if (isOpenRedelivery(order.redelivery, today)) return { mode: 'edit', block: null };
  const block = redeliverBlock(order, today);
  return block ? { mode: null, block } : { mode: 'new', block: null };
}

/** Why a new redelivery cannot be recorded for this order, or null (the order's own rules). */
export function redeliverBlock(order, today = londonToday()) {
  if (!order) return 'No customer order';
  const num = text(order.order_number) || 'This order';
  if (order.is_archived) return `${num} is archived — restore it first`;
  if (text(order.invoiced_status) === 'Invoiced' || order.invoiced === true) {
    return `${num} has been invoiced — raise a credit note`;
  }
  if (order.draft_invoice_no) {
    return `${num} has a draft invoice ${order.draft_invoice_no} waiting in Pricing & Costing — it was delivered and invoiced`;
  }
  const ours = text(order.excel_agri_delivery_terms);
  const theirs = text(order.supplier_delivery_terms);
  if (ours === THIRD_PARTY) return `${num} is Third-Party Delivery — a haulier carried it; record what happened with Update Delivery`;
  if (theirs === DIRECT_DELIVERY) return `${num} is Direct Delivery — the supplier carried it; record what happened with Update Delivery`;
  if (ours === CUSTOMER_COLLECTION && theirs === CUSTOMER_COLLECTION) {
    return `${num} is Customer Collection — the customer collects it, so there is no delivery of ours to refuse`;
  }
  const date = dayOf(order.delivery_date);
  if (!date) return `${num} has no delivery date — it has not been delivered`;
  if (date > String(today)) return `${num} is not delivered yet (due ${ukDay(date)}) — move the delivery date instead`;
  if (order.has_supply_pos !== false && text(order.delivery_status || NOT_DESPATCHED) === NOT_DESPATCHED) {
    return `${num} has not been despatched — move the delivery date instead`;
  }
  return null;
}

/*
 * ── The window's checks (and the save's) ─────────────────────────────────
 */

/**
 * Why this redelivery date cannot be used, or null. Blank is allowed (held,
 * date to follow). Never in the past, and after the day it was refused — the
 * refused day is the order's own, and a second leg the same day is a second
 * drop on the board, not a redelivery.
 */
export function redeliveryDateProblem({ date, refusedOn, orderDate, today = londonToday() } = {}) {
  const d = dayOf(date);
  if (date != null && text(date) !== '' && !d) return 'The redelivery date is not a date';
  if (!d) return null;
  if (d < String(today)) return `The redelivery date cannot be in the past (today is ${ukDay(today)})`;
  const after = [dayOf(refusedOn), dayOf(orderDate)].filter(Boolean).sort().pop();
  if (after && d <= after) return `The redelivery must be after the day it was refused (${ukDay(after)})`;
  return null;
}

/**
 * Why the Redelivery window cannot be saved, or null.
 * @param {object} p  reason, heldAtStock, heldAtAccountId, refusedOn,
 *   redeliveryDate, lineIds, orderDate, today, isNew
 */
export function redeliverySaveProblem({ reason, heldAtStock = false, heldAtAccountId = null, refusedOn, redeliveryDate,
  lineIds = [], orderDate = null, today = londonToday(), isNew = true } = {}) {
  if (!text(reason)) return 'Say why it was refused';
  if (text(reason).length > 2000) return 'The reason is too long (2,000 characters at most)';
  const account = Number(heldAtAccountId);
  if (!heldAtStock && !(Number.isInteger(account) && account > 0)) return 'Choose where it is held — STOCK (our site) or a contact';
  const refused = dayOf(refusedOn);
  if (!refused) return 'Give the day it was refused';
  if (refused > String(today)) return 'It cannot have been refused on a day still to come';
  if (dayOf(orderDate) && refused < dayOf(orderDate)) return `It cannot have been refused before its delivery date (${ukDay(orderDate)})`;
  if (isNew && !(Array.isArray(lineIds) && lineIds.length)) return 'Tick at least one line to redeliver';
  return redeliveryDateProblem({ date: redeliveryDate, refusedOn: refused, orderDate, today });
}

/** Why the order cannot be invoiced while a redelivery is open, or null. */
export function redeliveryInvoiceBlock(orderNumber, r, today = londonToday()) {
  const s = effectiveRedeliveryStatus(r, today);
  const num = text(orderNumber) || 'This order';
  if (s === REDELIVERY_STATUS_PLANNED) {
    return `${num} is waiting to be redelivered on ${ukDay(r.redelivery_date)} — invoice it once delivered`;
  }
  if (s === REDELIVERY_STATUS_HELD) {
    return `${num} is held at ${heldAtLabel(r) || 'a store'} waiting to be redelivered — invoice it once delivered`;
  }
  return null;
}

/** The invoice date a redelivered order defaults to: its latest delivered redelivery's day, or null. */
export function redeliveredInvoiceDate(redeliveries, today = londonToday()) {
  const days = (Array.isArray(redeliveries) ? redeliveries : [redeliveries])
    .filter(r => r && effectiveRedeliveryStatus(r, today) === REDELIVERY_STATUS_DELIVERED)
    .map(r => dayOf(r.redelivery_date)).filter(Boolean).sort();
  return days.length ? days[days.length - 1] : null;
}
